import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import {
  DEFAULT_UI_LOCALE,
  createTranslator,
  defaultDictionaries,
  formatLocaleDate,
  resolveLocale,
} from './translator'
import type { I18nContextValue, I18nProviderProps } from './types'

const defaultContext: I18nContextValue = {
  locale: DEFAULT_UI_LOCALE,
  fallbackLocale: DEFAULT_UI_LOCALE,
  supportedLocales: Object.keys(defaultDictionaries),
  t: createTranslator(DEFAULT_UI_LOCALE),
  setLocale: () => undefined,
  formatDate: (date, options) => formatLocaleDate(date, DEFAULT_UI_LOCALE, options),
}

const I18nContext = createContext<I18nContextValue>(defaultContext)

export function I18nProvider({
  locale = DEFAULT_UI_LOCALE,
  fallbackLocale = DEFAULT_UI_LOCALE,
  supportedLocales,
  dictionaries = defaultDictionaries,
  onLocaleChange,
  children,
}: I18nProviderProps) {
  const [activeLocale, setActiveLocale] = useState(locale)

  const updateLocale = useCallback(
    (nextLocale: string) => {
      const resolvedLocale = resolveLocale(nextLocale, dictionaries, fallbackLocale)
      setActiveLocale(resolvedLocale)
      onLocaleChange?.(resolvedLocale)
    },
    [dictionaries, fallbackLocale, onLocaleChange]
  )

  useEffect(() => {
    setActiveLocale(locale)
  }, [locale])

  const value = useMemo<I18nContextValue>(() => {
    const resolvedLocale = resolveLocale(activeLocale, dictionaries, fallbackLocale)
    const resolvedFallbackLocale = resolveLocale(fallbackLocale, dictionaries, DEFAULT_UI_LOCALE)
    const translator = createTranslator(resolvedLocale, dictionaries, resolvedFallbackLocale)

    return {
      locale: resolvedLocale,
      fallbackLocale: resolvedFallbackLocale,
      supportedLocales: supportedLocales ?? Object.keys(dictionaries),
      t: translator,
      setLocale: updateLocale,
      formatDate: (date, options) => formatLocaleDate(date, resolvedLocale, options),
    }
  }, [activeLocale, dictionaries, fallbackLocale, supportedLocales, updateLocale])

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
}

export function useI18n() {
  return useContext(I18nContext)
}
